import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend } from "chart.js";
import { Bar, Pie, Doughnut } from "react-chartjs-2";
import { getAdminSchools, getAdminSchoolInsights } from "@/actions/reportsAdmin";
import { Users, School, Search, BarChart3, PieChart } from "lucide-react";
import ReportHeader from "../components/ReportHeader";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

interface SchoolOption {
  slate_id: number;
  slate_name: string;
  cityName: string;
  areaName: string;
}

interface SourceStatusRow {
  status: string;
  [key: string]: string | number;
}

interface SubSourceRow {
  sub_source: string;
  total_count: number;
}

interface TotalInsightsRow {
  status: string;
  [key: string]: string | number;
}

interface SchoolInsightsData {
  assignedCount: number;
  sourceStatus: SourceStatusRow[];
  subSources: SubSourceRow[];
  totalInsights: TotalInsightsRow[];
  adminSources: string[];
}

const formatNumber = (num: number | string): string => {
  const n = typeof num === 'string' ? parseInt(num) : num;
  return n?.toLocaleString() || "0";
};

const generateColors = (count: number) => {
  const colors = [
    "rgba(79, 70, 229, 0.8)", "rgba(16, 185, 129, 0.8)", "rgba(245, 158, 11, 0.8)",
    "rgba(239, 68, 68, 0.8)", "rgba(59, 130, 246, 0.8)", "rgba(168, 85, 247, 0.8)",
    "rgba(236, 72, 153, 0.8)", "rgba(20, 184, 166, 0.8)", "rgba(249, 115, 22, 0.8)",
    "rgba(132, 204, 22, 0.8)", "rgba(99, 102, 241, 0.8)", "rgba(244, 63, 94, 0.8)",
  ];
  return Array.from({ length: count }, (_, i) => colors[i % colors.length]);
};

const ClientReportsReport: React.FC = () => {
  const [fromDate, setFromDate] = useState<string>(moment().startOf("month").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState<string>(moment().endOf("month").format("YYYY-MM-DD"));
  const [schools, setSchools] = useState<SchoolOption[]>([]);
  const [selectedSchool, setSelectedSchool] = useState<SchoolOption | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [schoolInsights, setSchoolInsights] = useState<SchoolInsightsData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [statusSearch, setStatusSearch] = useState<string>("");
  const [subSourceSearch, setSubSourceSearch] = useState<string>("");
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => { fetchSchools(); }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchSchools = async () => {
    try {
      const response = await getAdminSchools();
      if (response.success) setSchools((response.data as SchoolOption[]) || []);
    } catch (error) {
      console.error("Error fetching schools:", error);
    }
  };

  const fetchData = async () => {
    if (!selectedSchool) return;
    setLoading(true);
    try {
      const response = await getAdminSchoolInsights({
        from_date: fromDate,
        to_date: toDate,
        slate_id: selectedSchool.slate_id,
      });
      if (response.success) setSchoolInsights(response as unknown as SchoolInsightsData);
    } catch (error) {
      console.error("Error fetching school insights:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSchools = schools.filter((school) =>
    school.slate_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (school.cityName && school.cityName.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (school.areaName && school.areaName.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleSelectSchool = (school: SchoolOption) => {
    setSelectedSchool(school);
    setSearchQuery(school.slate_name);
    setShowSuggestions(false);
  };

  const handleSearch = () => {
    if (selectedSchool) {
      fetchData();
    }
  };

  // Get source columns from API response
  const getSourceColumns = (): string[] => {
    return schoolInsights?.adminSources || [];
  };

  // Check if a row has all zeros (filter helper)
  const hasNonZeroValues = (row: SourceStatusRow | TotalInsightsRow, columns: string[]): boolean => {
    return columns.some(col => {
      const val = row[col];
      return typeof val === 'number' ? val > 0 : parseInt(String(val)) > 0;
    });
  };

  const searchContent = (
    <div ref={searchRef} className="relative">
      <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5">
        <Search className="h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setShowSuggestions(true);
            if (!e.target.value) setSelectedSchool(null);
          }}
          onFocus={() => setShowSuggestions(true)}
          placeholder="Search school..."
          className="border-none bg-transparent focus:ring-0 text-sm text-gray-700 dark:text-gray-300 w-72 outline-none"
        />
        <button
          onClick={handleSearch}
          disabled={!selectedSchool}
          className="px-3 py-1 bg-brandColor text-white text-sm rounded-md hover:bg-brandColorDark disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Search
        </button>
      </div>
      {showSuggestions && searchQuery && filteredSchools.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
          {filteredSchools.slice(0, 10).map((school) => (
            <div
              key={school.slate_id}
              onClick={() => handleSelectSchool(school)}
              className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm"
            >
              <div className="font-medium text-gray-900 dark:text-white">{school.slate_name}</div>
              {(school.areaName || school.cityName) && (
                <div className="text-xs text-gray-500">
                  {[school.areaName, school.cityName].filter(Boolean).join(", ")}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const sourceColumns = getSourceColumns();

  // Filter out columns with zero totals
  const activeSourceColumns = sourceColumns.filter(col => {
    const total = schoolInsights?.sourceStatus?.reduce((sum, row) => sum + (parseInt(String(row[col])) || 0), 0) || 0;
    return total > 0;
  });

  // Compute chart data from schoolInsights
  const getStatusChartData = () => {
    if (!schoolInsights?.sourceStatus?.length) return null;
    const filteredRows = schoolInsights.sourceStatus.filter(row => hasNonZeroValues(row, activeSourceColumns));
    const labels = filteredRows.map(row => row.status);
    const data = filteredRows.map(row =>
      activeSourceColumns.reduce((sum, col) => sum + (parseInt(String(row[col])) || 0), 0)
    );
    return { labels, data };
  };

  const getSourceChartData = () => {
    if (!schoolInsights?.sourceStatus?.length || !activeSourceColumns.length) return null;
    const labels = activeSourceColumns;
    const data = activeSourceColumns.map(col =>
      schoolInsights.sourceStatus.reduce((sum, row) => sum + (parseInt(String(row[col])) || 0), 0)
    );
    return { labels, data };
  };

  const statusChartRawData = getStatusChartData();
  const sourceChartRawData = getSourceChartData();

  const statusChartData = statusChartRawData ? {
    labels: statusChartRawData.labels,
    datasets: [{ label: "Leads by Status", data: statusChartRawData.data, backgroundColor: generateColors(statusChartRawData.labels.length), borderWidth: 1 }],
  } : null;

  const sourceChartData = sourceChartRawData ? {
    labels: sourceChartRawData.labels,
    datasets: [{ label: "Leads by Source", data: sourceChartRawData.data, backgroundColor: generateColors(sourceChartRawData.labels.length), borderWidth: 1 }],
  } : null;

  return (
    <div className="h-full flex flex-col">
      <ReportHeader
        title="Client Reports"
        subtitle="School-specific insights"
        icon={<Users className="h-5 w-5 text-white" />}
        fromDate={fromDate}
        toDate={toDate}
        onFromDateChange={setFromDate}
        onToDateChange={setToDate}
        onApplyFilter={fetchData}
        beforeDateContent={searchContent}
      />

      <div className="flex-1 overflow-y-auto p-6">
        {!selectedSchool ? (
          <div className="text-center py-8">
            <School className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Search and select a school to view insights</p>
          </div>
        ) : loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandColor"></div>
          </div>
        ) : !schoolInsights ? (
          <div className="text-center py-8">
            <School className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No data available for the selected school</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Status Charts */}
            {statusChartData && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="background-container rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><BarChart3 className="h-5 w-5 text-brandColor" />Leads by Status (Bar)</h3>
                  <div className="h-80"><Bar data={statusChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} /></div>
                </div>
                <div className="background-container rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><PieChart className="h-5 w-5 text-brandColor" />Leads by Status (Pie)</h3>
                  <div className="h-80"><Pie data={statusChartData} options={{ responsive: true, maintainAspectRatio: false }} /></div>
                </div>
              </div>
            )}

            {/* Source Charts */}
            {sourceChartData && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="background-container rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><BarChart3 className="h-5 w-5 text-green-500" />Leads by Source (Bar)</h3>
                  <div className="h-80"><Bar data={sourceChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} /></div>
                </div>
                <div className="background-container rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><PieChart className="h-5 w-5 text-green-500" />Leads by Source (Doughnut)</h3>
                  <div className="h-80"><Doughnut data={sourceChartData} options={{ responsive: true, maintainAspectRatio: false }} /></div>
                </div>
              </div>
            )}

            {/* Source Status Distribution */}
            {schoolInsights.sourceStatus?.length > 0 && (
              <div className="background-container rounded-lg shadow">
                <div className="bg-gradient-to-r from-brandColor to-brand-hover px-6 py-4 rounded-t-lg flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-white">Source Status Distribution</h3>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search status..."
                      value={statusSearch}
                      onChange={(e) => setStatusSearch(e.target.value)}
                      className="pl-9 pr-4 py-2 text-sm rounded-lg border-0 focus:ring-2 focus:ring-white/50 w-48"
                    />
                  </div>
                </div>
                <div className="p-4 overflow-x-auto max-h-[400px] overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-50">Status</th>
                        <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase bg-gray-50">Total</th>
                        {activeSourceColumns.map((col) => (
                          <th key={col} className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap bg-gray-50">{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {schoolInsights.sourceStatus
                        .filter(row => hasNonZeroValues(row, activeSourceColumns))
                        .filter(row => !statusSearch || row.status.toLowerCase().includes(statusSearch.toLowerCase()))
                        .map((row, i) => {
                        const rowTotal = activeSourceColumns.reduce((sum, col) => sum + (parseInt(String(row[col])) || 0), 0);
                        return (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-sm text-left font-medium text-gray-900 sticky left-0 bg-white whitespace-nowrap">{row.status}</td>
                            <td className={`px-3 py-2 text-sm text-center font-bold ${rowTotal > 0 ? 'bg-blue-100 text-blue-800' : 'text-gray-500'}`}>
                              {formatNumber(rowTotal)}
                            </td>
                            {activeSourceColumns.map((col) => {
                              const val = parseInt(String(row[col])) || 0;
                              return (
                                <td key={col} className={`px-3 py-2 text-sm text-center ${val > 0 ? 'bg-green-100 text-green-800 font-semibold' : 'text-gray-500'}`}>
                                  {formatNumber(row[col])}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-gray-100">
                      <tr className="font-bold">
                        <td className="px-3 py-3 text-sm text-left text-gray-900 sticky left-0 bg-gray-100">Total</td>
                        <td className="px-3 py-3 text-sm text-center bg-blue-200 text-blue-900">
                          {formatNumber(schoolInsights.sourceStatus.reduce((sum, row) =>
                            sum + activeSourceColumns.reduce((rowSum, col) => rowSum + (parseInt(String(row[col])) || 0), 0), 0))}
                        </td>
                        {activeSourceColumns.map((col) => {
                          const total = schoolInsights.sourceStatus.reduce((sum, row) => sum + (parseInt(String(row[col])) || 0), 0);
                          return (
                            <td key={col} className={`px-3 py-3 text-sm text-center bg-green-200 text-green-900`}>
                              {formatNumber(total)}
                            </td>
                          );
                        })}
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* Sub Sources */}
            {schoolInsights.subSources?.length > 0 && (
              <div className="background-container rounded-lg shadow">
                <div className="bg-gradient-to-r from-brandColor to-brand-hover px-6 py-4 rounded-t-lg flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-white">Sub Sources</h3>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search sub source..."
                      value={subSourceSearch}
                      onChange={(e) => setSubSourceSearch(e.target.value)}
                      className="pl-9 pr-4 py-2 text-sm rounded-lg border-0 focus:ring-2 focus:ring-white/50 w-48"
                    />
                  </div>
                </div>
                <div className="p-4 overflow-x-auto max-h-[400px] overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">Sub Source</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">Total Count</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {schoolInsights.subSources
                        .filter(row => row.total_count > 0)
                        .filter(row => !subSourceSearch || row.sub_source.toLowerCase().includes(subSourceSearch.toLowerCase()))
                        .map((row, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm font-medium text-gray-900">{row.sub_source}</td>
                          <td className="px-4 py-2 text-sm text-gray-500">{formatNumber(row.total_count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
};

export default ClientReportsReport;
